import * as React from "react";
import {FC, useMemo} from "react";
import {ThemeProvider, Typography} from "@mui/material";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import Box from "@mui/material/Box";
import {useTranslation} from "react-i18next";
import Button from "@mui/material/Button";
import WinnersTable from "./winners-table/WinnersTable";
import {IWinnerTicket, IWish} from "../../../store/wish/Helper";
import {RootState, useAppSelector} from "../../../store";
import {formatDateWithSlash, getActiveWinners} from "../../../util/utils";
import {useIsUserWinner} from "../../../util/hooks/useWinnerHelpers";

interface IProps {
    wish: IWish
    winners: IWinnerTicket[]
    open: boolean
    handleDialogClose: () => void
}

const ViewWinnersDialog: FC<IProps> = (props) => {
    const {t} = useTranslation()
    const email = useAppSelector((state: RootState) => state.authReducer.loggedInUser.email)
    const isLoggedIn = Boolean(email)

    const activeWinners = getActiveWinners(props.winners, props.wish.winners)
    const isWinner = useIsUserWinner(activeWinners)

    const announcedOn = useMemo(() =>
            props.wish.end_time ? formatDateWithSlash(new Date(props.wish.end_time)) : null,
        [props.wish.end_time]
    )

    const statusMessage = useMemo(() => {
        const parts = []
        if (announcedOn) {
            parts.push(t('winners_announced_on', {date: announcedOn}))
        }
        if (isLoggedIn) {
            parts.push(isWinner ? t('you_are_one_of_lucky_winners') : t('you_are_not_one_of_lucky_winners'))
        }
        return parts.join(' ')
    }, [announcedOn, isLoggedIn, isWinner, t])

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog
                open={props.open}
                title={t('see_winners', {count: props.wish.number_of_winners})}
                maxWidth='md'
                handleDialogClose={props.handleDialogClose}
                actions={
                    <Button variant='outlined' onClick={props.handleDialogClose} color='primary'>
                        {t('close')}
                    </Button>
                }
                content={
                    <Box my={2}>
                        <Typography variant="body2" color="primary" sx={{mb: 2}}>
                            {statusMessage}
                        </Typography>
                        <WinnersTable wish={props.wish} winners={props.winners}/>
                    </Box>
                }
            />
        </ThemeProvider>
    )
}

export default ViewWinnersDialog
